// The hike a map's trail card shows, so the round warnings button beside the
// map's location control can follow it (SELECTED-HIKE-ALERTS.md): the card
// (TrailDataCard) publishes its hike, the button (SelectedHikeRouteAlerts)
// reads it. Plus the one click rule the button's difficult sections need on
// those maps: a click on a section's line, or any click while a section is
// shown with its popup, belongs to the warnings — the pages' own handlers
// (Explore's "click on empty map = deselect", the overlays' "no feature here =
// close the card") must leave the card alone, or the section would take the
// card, the button and itself with it.

import { reactive, readonly } from 'vue';
import { SECTION_LINE_LAYER_ID } from './sectionHighlights';

const state = reactive({ hikeId: null as string | null });

/** The hike the map's trail card shows; null when it shows none (or not a hike). */
export const selectedHikeCard = readonly(state);

/** A trail card's hike id: hikes only (a recording or planned trail has no warnings of its own). */
export function cardHikeId(type: unknown, id: unknown): string | null {
  if (type !== 'Hike') return null;
  if (id === null || id === undefined || id === '') return null;
  return String(id);
}

export function setSelectedHikeCard(hikeId: string | null): void {
  state.hikeId = hikeId;
}

const focusedSectionOwners = new Set<string | number>();
/**
 * A section was just let go. A map click lets a shown section go before the
 * page's own click handlers see it (its popup closes on Mapbox's `preclick`),
 * so that same click is still the warnings' own — until this task is done.
 */
let releasing = false;

/** A warnings list's highlighter shows (or let go of) a section with its popup. */
export function setSectionFocused(owner: string | number, focused: boolean): void {
  if (focused) {
    focusedSectionOwners.add(owner);
    return;
  }
  if (!focusedSectionOwners.delete(owner)) return;
  releasing = true;
  setTimeout(() => {
    releasing = false;
  }, 0);
}

export function anySectionFocused(): boolean {
  return focusedSectionOwners.size > 0;
}

interface ClickMap {
  getLayer?(id: string): unknown;
  queryRenderedFeatures?(point: unknown, options: { layers: string[] }): unknown[];
}

/**
 * Whether a map click is the warnings' own: on a difficult section's line
 * (it shows that section), or while a section is shown or the click has just
 * let it go (the click only lets it go). Never throws — a page's click
 * handling must go on regardless.
 */
export function routeAlertsOwnsMapClick(map: ClickMap | null | undefined, point: unknown): boolean {
  if (anySectionFocused() || releasing) return true;
  try {
    if (!map?.getLayer?.(SECTION_LINE_LAYER_ID) || !map.queryRenderedFeatures) return false;
    return map.queryRenderedFeatures(point, { layers: [SECTION_LINE_LAYER_ID] }).length > 0;
  } catch {
    return false;
  }
}
